import { tableDefaultProblem } from "./tableDefaults.ts";
export const fieldTypes = [
  "text",
  "textarea",
  "email",
  "number",
  "date",
  "select",
  "radio",
  "checkbox",
  "checklist",
  "title",
  "heading",
  "label",
  "table",
  "productItems",
] as const;
export type FieldType = (typeof fieldTypes)[number] | "decimal";
export interface TableLine {
  id: string;
  label: string;
}
export interface OrderField {
  id: string;
  type: FieldType;
  name: string;
  label: string;
  required: boolean;
  placeholder: string;
  help: string;
  options: string[];
  allowOther?: boolean;
  readOnly?: boolean;
  columns?: OrderField[];
  minimumCompletedRows?: number;
  requireEveryRow?: boolean;
  lines?: TableLine[];
  defaultFrom?: string;
  defaultValue?: string;
  defaultMode?: "never" | "always" | "whenFilled";
  defaultWhenColumnId?: string;
}
export const defaultSources = [
  { id: "accountNumber", label: "Account number" },
  { id: "customerName", label: "Customer name" },
  { id: "contactName", label: "Contact name" },
  { id: "address1", label: "Address line 1" },
  { id: "address2", label: "Address line 2" },
  { id: "address3", label: "Address line 3" },
  { id: "city", label: "City" },
  { id: "county", label: "County" },
  { id: "postcode", label: "Postcode" },
  { id: "telephone", label: "Telephone" },
  { id: "userName", label: "User raising the order" },
] as const;
export type DefaultSource = (typeof defaultSources)[number]["id"];
export interface OrderDefaults {
  accountNumber?: string | null;
  customerName?: string | null;
  contactName?: string | null;
  address1?: string | null;
  address2?: string | null;
  address3?: string | null;
  city?: string | null;
  county?: string | null;
  postcode?: string | null;
  telephone?: string | null;
  userName?: string | null;
}
export function defaultFieldValue(source: string | undefined, values?: OrderDefaults): string {
  if (!source || !values || !defaultSources.some((item) => item.id === source)) return "";
  return (values[source as DefaultSource] ?? "").trim();
}
export function orderDefaults(
  customer: {
    name?: string | null;
    accountNumber?: string | null;
    address1?: string | null;
    address2?: string | null;
    address3?: string | null;
    city?: string | null;
    county?: string | null;
    postcode?: string | null;
    contactNumber?: string | null;
    contacts?: Array<{
      defaultContact?: boolean;
      fullname?: string | null;
      title?: string | null;
      firstname?: string | null;
      lastname?: string | null;
    }>;
  },
  userName?: string | null,
): OrderDefaults {
  return {
    accountNumber: customer.accountNumber,
    customerName: customer.name,
    contactName: contactDisplayName(customer.contacts),
    address1: customer.address1,
    address2: customer.address2,
    address3: customer.address3,
    city: customer.city,
    county: customer.county,
    postcode: customer.postcode,
    telephone: customer.contactNumber,
    userName,
  };
}
function contactDisplayName(
  contacts: Array<{
    defaultContact?: boolean;
    fullname?: string | null;
    title?: string | null;
    firstname?: string | null;
    lastname?: string | null;
  }> | undefined,
): string {
  const contact = contacts?.find((item) => item.defaultContact) ?? contacts?.[0];
  if (!contact) return "";
  return (contact.fullname || [contact.title, contact.firstname, contact.lastname].filter(Boolean).join(" ")).trim();
}
export interface FormPage {
  id: string;
  title: string;
  rows: LayoutRow[];
}
export interface OrderForm {
  publishedVersionId?: number | null
  id: string;
  name: string;
  notifyEmail?: string;
  fields: OrderField[];
  updatedAt: string;
  rows?: LayoutRow[];
  pages?: FormPage[];
  schemaVersion?: number;
}
export const fieldNamePattern = /^[A-Za-z0-9_]+$/;
function defaultFieldName(type: FieldType): string {
  if (type === "productItems") return "OrderItems";
  if (type === "table") return "Table";
  if (type === "textarea") return "Textarea";
  if (type === "checklist") return "OptionList";
  return type.charAt(0).toUpperCase() + type.slice(1);
}
function defaultFieldLabel(type: FieldType): string {
  if (type === "productItems") return "Order items";
  if (type === "label") return "Label";
  if (type === "table") return "";
  if (type === "heading") return "Section heading";
  if (type === "checklist") return "Option list field";
  return `${type.charAt(0).toUpperCase()}${type.slice(1)} field`;
}
export function fieldCaption(field: { name?: string; label?: string; type?: string }): string {
  return field.label?.trim() || field.name?.trim() || "Field";
}
export function tableLines(field: { lines?: TableLine[] }): TableLine[] {
  return (field.lines ?? []).filter((line) => line.label.trim());
}
export function syncTableLines(text: string, current: TableLine[] = []): TableLine[] {
  if (!text) return [];
  return text.split("\n").map((label, index) => ({
    id: current[index]?.id ?? crypto.randomUUID(),
    label,
  }));
}
export function newField(type: FieldType): OrderField {
  const name = defaultFieldName(type);
  return {
    id: crypto.randomUUID(),
    type,
    name,
    label: defaultFieldLabel(type),
    columns: type === "table" ? [] : undefined,
    lines: type === "table" ? [] : undefined,
    minimumCompletedRows: type === "table" ? 0 : undefined,
    requireEveryRow: type === "table" ? false : undefined,
    required: false,
    placeholder: "",
    help: "",
    options: ["Option 1", "Option 2"],
    allowOther: false,
  };
}
export function validateForm(form: OrderForm): string | null {
  if (form.fields.filter(f => f.type === "productItems").length > 1) return "Use one Order items field per form.";
  if (!form.name.trim()) return "Give your form a name.";
  if (!form.fields.length) return "Add at least one field.";
  for (const table of form.fields.filter(f => f.type === "table")) {
    const defaultProblem = tableDefaultProblem(table);
    if (defaultProblem) return defaultProblem;
    const minimum = table.minimumCompletedRows ?? 0;
    if (!Number.isInteger(minimum) || minimum < 0 || minimum > tableLines(table).length)
      return "Minimum completed rows must be a whole number between 0 and the number of table rows.";
    if (minimum > 0 && !table.requireEveryRow && !(table.columns ?? []).some(c => c.required))
      return "Choose at least one required column when setting a minimum completed row count.";
  }
  const allFields = form.fields.flatMap((f) => [f, ...(f.columns ?? [])]);
  if (form.fields.some((f) => f.type === "table" && (!f.columns?.length || !tableLines(f).length)))
    return "Tables need at least one column and one row label.";
  if (allFields.some((f) => !f.name?.trim()))
    return "Every field needs a name.";
  if (allFields.some((f) => !["title", "heading", "label"].includes(f.type) && !fieldNamePattern.test(f.name.trim())))
    return "Field names use letters, numbers and underscores, with no spaces.";
  if (
    allFields.some(
      (f) =>
        ["select", "radio", "checklist"].includes(f.type) &&
        (!f.options.length ||
          f.options.some((o) => !o.trim()) ||
          new Set(f.options).size !== f.options.length),
    )
  )
    return "Choice fields need unique, non-empty options.";
  const ids = formPages(form).flatMap((page) =>
    page.rows.flatMap((r) => r.columns.flatMap((c) => c.fieldIds)),
  );
  if (
    ids.length !== form.fields.length ||
    new Set(ids).size !== ids.length ||
    ids.some((id) => !form.fields.some((f) => f.id === id))
  )
    return "Each field must appear once in the layout.";
  return null;
}
export function reorder<T>(items: T[], from: number, to: number): T[] {
  const next = [...items];
  if (from < 0 || to < 0 || from >= next.length || to >= next.length)
    return next;
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

export interface LayoutColumn {
  id: string;
  width: number;
  fieldIds: string[];
}
export interface LayoutRow {
  id: string;
  columns: LayoutColumn[];
}
export const rowLayouts = {
  "Full width": [12],
  "Two columns": [6, 6],
  "Three columns": [4, 4, 4],
  "Wide left": [8, 4],
  "Wide right": [4, 8],
};
export function newRow(widths: number[] = [12]): LayoutRow {
  return {
    id: crypto.randomUUID(),
    columns: widths.map((width) => ({
      id: crypto.randomUUID(),
      width,
      fieldIds: [],
    })),
  };
}
function legacyRows(form: OrderForm): LayoutRow[] {
  if (form.rows?.length) return form.rows;
  return form.fields.map((f) => ({
    id: `row-${f.id}`,
    columns: [{ id: `column-${f.id}`, width: 12, fieldIds: [f.id] }],
  }));
}
export function formPages(form: OrderForm): FormPage[] {
  if (form.pages?.length) return form.pages;
  return [{ id: "page-1", title: "Page 1", rows: legacyRows(form) }];
}
export function layoutRows(form: OrderForm): LayoutRow[] {
  return formPages(form).flatMap((page) => page.rows);
}
export function normalizeForm(form: OrderForm): OrderForm {
  const source = form.pages?.length
    ? form.pages
    : [{ id: form.id ? `page-${form.id}` : "page-1", title: "Page 1", rows: legacyRows(form) }];
  const pages = source.map((page, index) => ({
    id: page.id || `page-${index + 1}`,
    title: page.title ?? `Page ${index + 1}`,
    rows: page.rows?.length ? page.rows : [newRow()],
  }));
  return {
    ...form,
    fields: form.fields.map(namedField),
    schemaVersion: 3,
    pages,
    rows: pages.flatMap((page) => page.rows),
  };
}
function namedField(field: OrderField): OrderField {
  const name = field.name?.trim() ? field.name : field.label ?? "";
  const label =
    ["label", "heading"].includes(field.type) && !field.label?.trim() && name.trim() && !fieldNamePattern.test(name.trim())
      ? name
      : field.label ?? "";
  const defaultFrom =
    ["text", "textarea"].includes(field.type) && defaultSources.some((item) => item.id === field.defaultFrom)
      ? field.defaultFrom
      : undefined;
  const readOnly = ["text", "textarea", "email", "number", "date"].includes(field.type) ? Boolean(field.readOnly) : undefined;
  return {
    ...field,
    name,
    label,
    defaultFrom,
    readOnly,
    columns: field.columns?.map(namedField),
    lines: field.lines?.map((line) => ({
      id: line.id || crypto.randomUUID(),
      label: line.label ?? "",
    })),
  };
}
export function updatePages(form: OrderForm, pages: FormPage[]): OrderForm {
  return normalizeForm({ ...form, pages, rows: undefined });
}

export function blankOrderForm(): OrderForm {
  return normalizeForm({
    id: "",
    name: "",
    fields: [],
    updatedAt: "",
  });
}

export function parseStoredForm(
  json: string | null | undefined,
  fallback: { id?: string; name?: string; updatedAt?: string } = {},
): OrderForm | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as Partial<OrderForm>;
    if (!parsed || !Array.isArray(parsed.fields)) return null;
    return normalizeForm({
      id: String(fallback.id ?? parsed.id ?? ""),
      name: fallback.name || parsed.name || "",
      notifyEmail: parsed.notifyEmail ?? "",
      fields: parsed.fields,
      rows: parsed.rows,
      pages: parsed.pages,
      updatedAt: fallback.updatedAt || parsed.updatedAt || "",
    });
  } catch {
    return null;
  }
}

export function parseOrderAnswers(
  json: string | null | undefined,
): Record<string, string | string[]> {
  try {
    const parsed = JSON.parse(json || "{}") as Record<string, unknown>;
    const answers: Record<string, string | string[]> = {};
    for (const [key, value] of Object.entries(parsed ?? {})) {
      if (Array.isArray(value)) answers[key] = value.map((item) => String(item));
      else if (value != null && value !== "") answers[key] = String(value);
    }
    return answers;
  } catch {
    return {};
  }
}
export function changeRowLayout(row: LayoutRow, widths: number[]): LayoutRow {
  const columns = widths.map((width, i) => ({
    id: row.columns[i]?.id ?? crypto.randomUUID(),
    width,
    fieldIds: [...(row.columns[i]?.fieldIds ?? [])],
  }));
  columns[columns.length - 1].fieldIds.push(
    ...row.columns.slice(widths.length).flatMap((c) => c.fieldIds),
  );
  return { ...row, columns };
}
export function placeField(
  form: OrderForm,
  fieldId: string,
  columnId: string,
  beforeId?: string,
): OrderForm {
  const pages = structuredClone(formPages(form));
  const column = pages
    .flatMap((page) => page.rows)
    .flatMap((row) => row.columns)
    .find((c) => c.id === columnId);
  if (
    !column ||
    !form.fields.some((f) => f.id === fieldId) ||
    fieldId === beforeId
  )
    return form;
  pages.forEach((page) =>
    page.rows.forEach((row) =>
      row.columns.forEach(
        (c) => (c.fieldIds = c.fieldIds.filter((id) => id !== fieldId)),
      ),
    ),
  );
  const index = beforeId ? column.fieldIds.indexOf(beforeId) : -1;
  column.fieldIds.splice(
    index < 0 ? column.fieldIds.length : index,
    0,
    fieldId,
  );
  return normalizeForm({ ...form, pages, rows: undefined });
}

// Missing table settings preserve the original required-on-every-row behaviour.
export function tableValidationErrors(field: OrderField, answer: (key: string) => string): Record<string, string> {
  const errors: Record<string, string> = {};
  const columns = field.columns ?? [];
  const lines = tableLines(field);
  const every = field.requireEveryRow ?? field.minimumCompletedRows === undefined;
  let completed = 0;
  for (const line of lines) {
    const key = (column: OrderField) => `${field.id}.${line.id}.${column.id}`;
    const used = every || columns.some(c => c.required && answer(key(c)).trim() !== "");
    if (!used) continue;
    let complete = true;
    for (const column of columns) {
      if (column.required && !answer(key(column)).trim()) {
        errors[key(column)] = `${line.label}: ${fieldCaption(column)} is required for this row`;
        complete = false;
      }
    }
    if (complete) completed++;
  }
  const minimum = field.minimumCompletedRows ?? 0;
  if (completed < minimum && lines[0] && columns[0]) {
    errors[field.id] = `Complete at least ${minimum} row${minimum === 1 ? "" : "s"} in ${fieldCaption(field)}`;
  }
  return errors;
}

export const decimalPattern = /^[+-]?(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/;

export function formatDecimalValue(value: string): string {
  const text = value.trim();
  if (!text || !decimalPattern.test(text)) return value;
  const [whole, fraction = ""] = text.split(".");
  const integer = whole === "" ? "0" : whole === "-" ? "-0" : whole === "+" ? "+0" : whole;
  return `${integer}.${fraction.padEnd(2, "0")}`;
}
